/**
 * Shared <AgentNew scope> — Create Agent mode-selector chooser.
 *
 * PR-B: agent-new shared-component refactor (2026-05-12).
 *
 * Mounted by:
 *   - routes/agents/new.tsx                  (scope="tenant")
 *   - routes/sysadmin/catalog.agents.new.tsx (scope="sysadmin")
 *
 * Three modes:
 *   1. Build with AI (recommended) → builder route (per adapter)
 *   2. Quick Create → inline form (slug field rendered on sysadmin only)
 *   3. Import → paste JSON
 *
 * ─── SCOPE-DEVIATION TABLE ────────────────────────────────────────────────
 *
 *   Bucket A — Hook swaps
 *   ─────────────────────
 *     tenant   : useCreateAgent          → POST /custom-agents
 *     sysadmin : useCreateCatalogAgent   → POST /admin/platform/catalog/agents
 *
 *   Bucket B — Affordances rendered only on one side
 *   ───────────────────────────────────────────────
 *     Sysadmin only : Slug field in Quick Create (with auto-derive from name)
 *                     "Create System Agent" title
 *                     "Back to agent catalog" back-button title
 *                     Sysadmin import JSON placeholder (slug/functions/workflows)
 *                     "name, slug, system prompt" copy in Quick Create card
 *     Tenant only   : "Create Agent" title
 *                     Simpler JSON import placeholder (no slug/functions/workflows)
 *                     "name, system prompt" copy in Quick Create card
 *
 *   Bucket C — Save payload differences
 *   ─────────────────────────────────────
 *     Quick Create body
 *       tenant   : { name, description?, systemPrompt, category }
 *       sysadmin : { slug, displayName, description, systemPrompt, category }
 *                  (description defaults to name; slug auto-derives if blank)
 *
 *     Import body
 *       tenant   : CreateAgentInput          (tools/skills/tags/triggerModes)
 *       sysadmin : CreateCatalogAgentInput   (toolSlugs/functionKeys/skillSlugs/workflowSlugs;
 *                                             legacy key precedence preserved)
 *
 *   Bucket D — Post-save navigation
 *   ────────────────────────────────
 *     tenant   : navigate('/agents')                         // back to list
 *     sysadmin : navigate('/sysadmin/catalog/agents/builder',
 *                         { search: { id: <newId>, model: undefined } })
 *                                                            // straight into editor
 *
 * Every deviation above is funnelled through the adapter at
 * `./AgentNew.adapters.tsx`. The body below is scope-pure — it
 * conditionally renders only on `adapter.showSlugField` (one
 * scope-conditional render) and reads strings from the adapter.
 */
import { useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import {
  ArrowLeft,
  Sparkles,
  Settings2,
  Upload,
  ChevronRight,
  Save,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import {
  AGENT_CATEGORY_LABELS,
  type AgentCategory,
} from '@/lib/mock-agents-data';

import { slugify } from './AgentNew.helpers';
import { getAdapter } from './AgentNew.adapters';
import type { AgentNewScope, CreateMode } from './AgentNew.types';

interface AgentNewProps {
  scope: AgentNewScope;
}

export function AgentNew({ scope }: AgentNewProps) {
  const adapter = getAdapter(scope);
  const navigate = useNavigate();
  const [mode, setMode] = useState<CreateMode>('select');
  const { saveInFlight, save } = adapter.useSaveAgent();

  // Quick create state
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState<AgentCategory>('custom');
  const [systemPrompt, setSystemPrompt] = useState('');

  // Import state
  const [importJson, setImportJson] = useState('');

  const canSaveQuick = name.trim() && systemPrompt.trim();

  const handleQuickSave = () => {
    if (!canSaveQuick) return;
    const body = adapter.buildQuickCreateBody({
      name,
      slug,
      description,
      category,
      systemPrompt,
    });
    save({
      agentName: name,
      body,
      onSuccess: (resp) => {
        toast.success(`Agent "${name}" created`);
        const next = adapter.navigateAfterSave(resp);
        // TanStack Router's typed navigate is strict; we know the
        // adapter's `to` corresponds to a real route the consumer is
        // wired to. Cast at the boundary only.
        navigate({
          to: next.to,
          search: next.search,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } as any);
      },
      onError: (e) => toast.error('Create failed', { description: e.message }),
    });
  };

  const handleImport = () => {
    if (!importJson.trim()) return;
    const parsed = adapter.buildImportBody(importJson);
    if (!parsed) {
      toast.error(
        scope === 'sysadmin'
          ? 'Import requires valid JSON with "name" and "systemPrompt" fields'
          : 'Import requires both "name" and "systemPrompt" fields',
      );
      return;
    }
    save({
      agentName: parsed.agentName,
      body: parsed.body,
      onSuccess: (resp) => {
        toast.success(`Agent "${parsed.agentName}" imported`);
        const next = adapter.navigateAfterSave(resp);
        navigate({
          to: next.to,
          search: next.search,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } as any);
      },
      onError: (e) => toast.error('Import failed', { description: e.message }),
    });
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="border-b border-border/40 bg-background px-6 py-3 flex items-center gap-3 shrink-0">
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          title={adapter.backButtonTitle}
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          onClick={() => navigate({ to: adapter.backRoute } as any)}
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <Separator orientation="vertical" className="h-5" />
        <div>
          <h1 className="text-lg font-semibold">{adapter.pageTitle}</h1>
          <p className="text-xs text-muted-foreground">{adapter.pageSubtitle}</p>
        </div>
      </div>

      <div className="flex-1 overflow-auto p-6">
        {mode === 'select' && (
          <div className="max-w-2xl mx-auto space-y-4">
            {/* Build with AI */}
            <Card
              className="cursor-pointer hover:border-primary/30 transition-colors group"
              onClick={() =>
                navigate({
                  to: adapter.builderRoute,
                  search: adapter.builderSearch,
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                } as any)
              }
            >
              <CardContent className="pt-5 pb-4">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                    <Sparkles className="h-6 w-6 text-primary" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="text-base font-semibold">Build with AI</h3>
                      <Badge className="text-[9px]">Recommended</Badge>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">
                      Describe what you need and Argus Architect will design
                      the agent for you. Conversational, guided, and
                      intelligent.
                    </p>
                  </div>
                  <ChevronRight className="h-5 w-5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity mt-1" />
                </div>
              </CardContent>
            </Card>

            {/* Quick Create */}
            <Card
              className="cursor-pointer hover:border-primary/30 transition-colors group"
              onClick={() => setMode('quick')}
            >
              <CardContent className="pt-5 pb-4">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-muted flex items-center justify-center shrink-0">
                    <Settings2 className="h-6 w-6 text-muted-foreground" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="text-base font-semibold">Quick Create</h3>
                      <Badge variant="secondary" className="text-[9px]">
                        For power users
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">
                      {adapter.quickCreateCopy}
                    </p>
                  </div>
                  <ChevronRight className="h-5 w-5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity mt-1" />
                </div>
              </CardContent>
            </Card>

            {/* Import */}
            <Card
              className="cursor-pointer hover:border-primary/30 transition-colors group"
              onClick={() => setMode('import')}
            >
              <CardContent className="pt-5 pb-4">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-muted flex items-center justify-center shrink-0">
                    <Upload className="h-6 w-6 text-muted-foreground" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-base font-semibold">
                      Import from JSON
                    </h3>
                    <p className="text-sm text-muted-foreground mt-1">
                      Paste an agent configuration JSON to import an existing
                      agent definition.
                    </p>
                  </div>
                  <ChevronRight className="h-5 w-5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity mt-1" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {mode === 'quick' && (
          <div className="max-w-2xl mx-auto space-y-6">
            <Button
              variant="ghost"
              size="sm"
              className="gap-1 -ml-2"
              onClick={() => setMode('select')}
            >
              <ArrowLeft className="h-3.5 w-3.5" /> Back to options
            </Button>

            <Card>
              <CardContent className="pt-4 space-y-4">
                <div className="space-y-1.5">
                  <Label className="text-xs">
                    Agent Name<span className="text-red-500 ml-0.5">*</span>
                  </Label>
                  <Input
                    value={name}
                    onChange={(e) => {
                      setName(e.target.value);
                      // Sysadmin: auto-populate slug unless user has typed one.
                      if (adapter.showSlugField && !slug) {
                        setSlug(slugify(e.target.value));
                      }
                    }}
                    className="h-8 text-xs"
                    placeholder="e.g., Boiler Room Monitor"
                    maxLength={60}
                  />
                </div>

                {adapter.showSlugField && (
                  <div className="space-y-1.5">
                    <Label className="text-xs">
                      Slug<span className="text-red-500 ml-0.5">*</span>
                    </Label>
                    <Input
                      value={slug}
                      onChange={(e) => setSlug(e.target.value)}
                      className="h-8 text-xs font-mono"
                      placeholder="boiler-room-monitor"
                      title="Catalog identity key — kebab-case, immutable after creation"
                    />
                  </div>
                )}

                <div className="space-y-1.5">
                  <Label className="text-xs">Description</Label>
                  <Textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    rows={2}
                    className="text-xs"
                    placeholder="What does this agent do?"
                    maxLength={300}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Category</Label>
                  <Select
                    value={category}
                    onValueChange={(v) => setCategory(v as AgentCategory)}
                  >
                    <SelectTrigger className="h-8 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(AGENT_CATEGORY_LABELS).map(([k, v]) => (
                        <SelectItem key={k} value={k}>
                          {v.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">
                    System Prompt<span className="text-red-500 ml-0.5">*</span>
                  </Label>
                  <Textarea
                    value={systemPrompt}
                    onChange={(e) => setSystemPrompt(e.target.value)}
                    rows={8}
                    className="font-mono text-xs"
                    placeholder="Define the agent's behavior, rules, and capabilities..."
                  />
                </div>
                <div className="flex items-center gap-2 pt-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setMode('select')}
                  >
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    disabled={!canSaveQuick || saveInFlight}
                    onClick={handleQuickSave}
                  >
                    <Save className="h-3.5 w-3.5 mr-1.5" />
                    {saveInFlight ? 'Creating…' : 'Create Agent'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {mode === 'import' && (
          <div className="max-w-2xl mx-auto space-y-6">
            <Button
              variant="ghost"
              size="sm"
              className="gap-1 -ml-2"
              onClick={() => setMode('select')}
            >
              <ArrowLeft className="h-3.5 w-3.5" /> Back to options
            </Button>

            <Card>
              <CardContent className="pt-4 space-y-4">
                <div className="space-y-1.5">
                  <Label className="text-xs">Agent Configuration (JSON)</Label>
                  <Textarea
                    value={importJson}
                    onChange={(e) => setImportJson(e.target.value)}
                    rows={12}
                    className="font-mono text-xs"
                    placeholder={adapter.importPlaceholder}
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setMode('select')}
                  >
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    disabled={!importJson.trim() || saveInFlight}
                    onClick={handleImport}
                  >
                    <Upload className="h-3.5 w-3.5 mr-1.5" />
                    {saveInFlight ? 'Importing…' : 'Import Agent'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}
